import React, { useEffect, useRef, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { rccColors } from "../colors";
import D3SingleLineChart from "./D3SingleLineChart";
import { appService } from "../../App/app.service";
import { getDefaultSalesType } from "../../util/customFunctions";
import { getNPriorQuarters } from "../../util/fiscalQuarterFunctions";
import RccCard from "../RccCard";

/**
 * Displays trend of total ACV of lost opportunities.
 * Inputs: Redux state (closedLostOppData, closedLostOppFilters, company, user).
 * Outputs: Single line chart of lost ACV over quarters.
 */
function ShiftInValueOfOppLost({ title, redirectToDashboard }) {
  const dispatch = useDispatch();
  const fetchingRef = useRef(false);
  const [appliedFilters, setAppliedFilters] = useState([]);

  // Get data from Redux store - using same data source as LostReasonTrend
  const company = useSelector((state) => state.app.company);
  const user = useSelector((state) => state.app.user || {});
  const closedLostOppFilters = useSelector(
    (state) => state.app.closedLostOppFilters || ""
  );
  const closedLostOppData = useSelector(
    (state) => state.app.closedLostOppData || ""
  );

  const valueLabel = useSelector((state) => {
    const label = state.app.user?.Value_Label;
    // Ensure valueLabel is always a string, not an object
    return typeof label === "string" ? label : "";
  });

  // Helper to check if data is loaded
  const isDataLoaded =
    Array.isArray(closedLostOppData) && closedLostOppData.length > 3;
  const isLoading =
    (!isDataLoaded || closedLostOppFilters === "") && user.currentFiscalQuarter;

  // --- Fetch Logic ---
  useEffect(() => {
    if (
      !fetchingRef.current &&
      (closedLostOppFilters === "" || closedLostOppData === "")
    ) {
      fetchingRef.current = true;

      // Fetch Filters
      dispatch({ type: "get_closed_lost_opp_analysis_filters_request" });
      appService.getClosedLostOppAnalysisFilters().then(
        (resp) => {
          dispatch({
            type: "get_closed_lost_opp_analysis_filters_success",
            json: resp,
          });

          // Build Filters
          const currentQuarter = user.currentFiscalQuarter;
          const priorQuarters = getNPriorQuarters(currentQuarter, 3).reverse();
          const defaultFiscalQuarters = [...priorQuarters, currentQuarter];

          const defaultOppType = getDefaultSalesType(
            resp.message.filter((i) => i.type === "Type"),
            "Type"
          );
          const defaultSolutionCat = getDefaultSalesType(
            resp.message.filter((i) => i.type === "Solution_Cat"),
            "Solution_Cat"
          );
          const earliestLostStage = resp.message
            .filter(
              (item) =>
                item.type === "Earliest_Lost_Stage" &&
                item.selected_by_default === "Y"
            )
            .map((i) => i.stage_name);

          let filters = [
            {
              name: "closed_fiscal_quarter",
              value: defaultFiscalQuarters,
              string: true,
            },
          ];

          if (earliestLostStage.length >= 1) {
            filters.push({
              name: "Earliest_Lost_Stage",
              value: earliestLostStage,
              string: true,
            });
          }

          if (company === "C0008") {
            const salesFilters = getDefaultSalesType(
              resp.message.filter((s) => s.type === "cross_sell1__c"),
              "cross_sell1__c"
            );
            const dealsFilters = getDefaultSalesType(
              resp.message.filter((s) => s.type === "Type"),
              "Type"
            );
            if (salesFilters[0] !== "All")
              filters.push({ name: "cross_sell1__c", value: salesFilters });
            if (dealsFilters[0] !== "All")
              filters.push({ name: "Type", value: dealsFilters });
          }

          if (defaultOppType[0] !== "All")
            filters.push({ name: "Type", value: defaultOppType });
          if (defaultSolutionCat[0] !== "All")
            filters.push({ name: "Solution_Cat", value: defaultSolutionCat });

          filters = filters.filter(
            (f) =>
              (!Array.isArray(f.value) && f.value !== "All") ||
              !f.value.includes("All")
          );

          setAppliedFilters(filters);
          dispatch({ type: "current_selected_filters", filters });

          // Fetch Data
          dispatch({ type: "get_closed_lost_opp_analysis_data_request" });
          appService
            .getClosedLostOppAnalysisData(filters, "closed_fiscal_quarter")
            .then(
              (dataResp) => {
                // Add OppNo counter to table data (index 1)
                let counter = 0;
                let temp = dataResp.message[1].map((i) => ({
                  ...i,
                  OppNo: ++counter,
                }));

                const processedData = {
                  ...dataResp,
                  message: [
                    dataResp.message[0],
                    temp,
                    dataResp.message[2],
                    dataResp.message[3],
                  ],
                };

                fetchingRef.current = false;
                dispatch({
                  type: "get_closed_lost_opp_analysis_data_success",
                  json: processedData,
                });
              },
              (error) => {
                fetchingRef.current = false;
                dispatch({
                  type: "get_closed_lost_opp_analysis_failure",
                  error:
                    typeof error === "object" ? "Something went wrong" : error,
                });
              }
            );
        },
        (error) => {
          fetchingRef.current = false;
          dispatch({
            type: "get_closed_lost_opp_analysis_filters_failure",
            error: typeof error === "object" ? "Something went wrong" : error,
          });
        }
      );
    }
  }, [company, dispatch, closedLostOppFilters, closedLostOppData, user]);

  // Transform data for chart - aggregate total ACV per quarter
  const chartData = useMemo(() => {
    if (!isDataLoaded) {
      return [];
    }

    // dataArray[1] contains tableData with individual opportunity records
    const tableData = closedLostOppData[1] || [];

    if (!Array.isArray(tableData) || tableData.length === 0) {
      return [];
    }

    // Determine which quarter field to use (default to closed_fiscal_quarter)
    const quarterField = "closed_fiscal_quarter";

    // Aggregate total ACV per quarter
    const acvByQuarter = {};

    tableData.forEach((item) => {
      const quarter = item[quarterField];
      // ACV might be stored as ACV, acv, or Value_Label field
      const acv = item.ACV || item.acv || item.Value || 0;

      if (quarter && acv !== null && acv !== undefined && !isNaN(acv)) {
        if (!acvByQuarter[quarter]) {
          acvByQuarter[quarter] = 0;
        }
        acvByQuarter[quarter] += Number(acv);
      }
    });

    // Transform to chart format: [{ quarter: string, value: number }]
    return Object.entries(acvByQuarter)
      .map(([quarter, totalAcv]) => ({
        quarter,
        value: Math.round(totalAcv),
      }))
      .sort((a, b) => {
        // Sort by quarter string (e.g., "2024-Q1" < "2024-Q2")
        return a.quarter.localeCompare(b.quarter);
      });
  }, [closedLostOppData, isDataLoaded]);

  const handleRedirect = () => {
    console.log("Redirecting to Lost Opps Dashboard...");
  };

  const handleZoom = () => {
    console.log("Zooming Lost Opps Chart...");
  };

  return (
    <RccCard
      title={title || `Shift in ${valueLabel} of Opp Lost`}
      isLoading={isLoading}
      redirectToDashboard={redirectToDashboard || "Closed Lost Analysis"}
      onZoomClick={handleZoom}
      appliedFilters={appliedFilters}
      focusVariant={"current"}
    >
      <div
        style={{
          flex: 1,
          width: "100%",
          minHeight: "300px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {chartData.length > 0 ? (
          <D3SingleLineChart
            data={chartData}
            options={{
              lineColor: rccColors.stackedBarPairedPalette[0].color,
              lineStyle: "dotted",
              legendLabel: "Opps Lost",
              yAxisFormatter: (v) => {
                if (v >= 1000000) {
                  return `${(v / 1000000).toFixed(1)}M`;
                }
                if (v >= 1000) {
                  return `${(v / 1000).toFixed(1)}K`;
                }
                return v.toString();
              },
              valueFormatter: (v) => {
                if (v >= 1000000) {
                  return `${(v / 1000000).toFixed(1)}M`;
                }
                if (v >= 1000) {
                  return `${(v / 1000).toFixed(1)}K`;
                }
                return Math.round(v).toString();
              },
              showValueLabels: true,
              showLegend: true,
            }}
          />
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              color: rccColors.neutral[400],
            }}
          >
            No data available
          </div>
        )}
      </div>
    </RccCard>
  );
}

export default ShiftInValueOfOppLost;
